import React, { useState } from 'react';
import { Bookmark, X, AlertTriangle, Check } from 'lucide-react';
import {
  RackReservation,
  EquipmentSlotItem,
  validateReservationPlacement,
} from '../../utils/rackReservation.logic';

interface RackReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<RackReservation, 'id'>) => { success: boolean; error?: string } | Promise<{ success: boolean; error?: string }>;
  totalU: number;
  equipmentList: EquipmentSlotItem[];
  reservations: RackReservation[];
  initialSlot?: number;
  initialMount?: string;
  theme?: 'dark' | 'light';
}

export function RackReservationModal({
  isOpen,
  onClose,
  onSubmit,
  totalU,
  equipmentList,
  reservations,
  initialSlot = 1,
  initialMount = 'Front',
  theme: _theme = 'dark',
}: RackReservationModalProps) {
  const [uStart, setUStart] = useState<number>(initialSlot);
  const [uHeight, setUHeight] = useState<number>(2);
  const [tenant, setTenant] = useState<string>('mine');
  const [description, setDescription] = useState<string>('');
  const [face, setFace] = useState<'front' | 'rear' | 'both'>(() => {
    const m = (initialMount || '').toLowerCase();
    if (m === 'rear' || m === 'back') return 'rear';
    if (m === 'front') return 'front';
    return 'both';
  });
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleValidateAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validation = validateReservationPlacement(
      reservations,
      equipmentList,
      Number(uStart),
      Number(uHeight),
      totalU,
      undefined,
      face
    );

    if (!validation.valid) {
      setError(validation.conflictReason || 'Ошибка валидации бронирования');
      return;
    }

    const result = await onSubmit({
      u_start: Number(uStart),
      u_height: Number(uHeight),
      tenant: tenant.trim() || 'mine',
      description: description.trim() || 'Резерв места',
      face,
    });

    if (!result.success) {
      setError(result.error || 'Не удалось сохранить бронирование');
      return;
    }

    onClose();
  };

  const endU = Number(uStart) + Number(uHeight) - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reservation-modal-title"
    >
      <div
        className="w-full max-w-md rounded-lg border shadow-2xl overflow-hidden font-mono"
        style={{
          backgroundColor: 'var(--bg-panel)',
          borderColor: 'var(--border-color)',
          color: 'var(--text-primary)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{
            borderColor: 'var(--border-color)',
            backgroundColor: 'var(--bg-app)',
          }}
        >
          <div className="flex items-center gap-2">
            <Bookmark size={16} className="text-amber-500" aria-hidden="true" />
            <h3 id="reservation-modal-title" className="text-xs font-bold uppercase tracking-wider text-text-primary">
              Зарезервировать юниты в стойке
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-text-muted hover:text-text-primary transition-colors"
            aria-label="Закрыть"
          >
            <X size={15} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleValidateAndSubmit} className="p-4 space-y-3.5 text-xs">
          {error && (
            <div className="flex items-start gap-2 p-2.5 rounded bg-red-500/10 border border-red-500/40 text-red-400 text-[11px]">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Unit selection */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">
                Начальный юнит (U)
              </label>
              <input
                type="number"
                min={1}
                max={totalU}
                value={uStart}
                onChange={(e) => setUStart(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-2.5 py-1.5 rounded border border-border bg-bg-input text-text-primary text-xs focus:outline-none focus:border-amber-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">
                Высота (U)
              </label>
              <input
                type="number"
                min={1}
                max={totalU - uStart + 1}
                value={uHeight}
                onChange={(e) => setUHeight(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-2.5 py-1.5 rounded border border-border bg-bg-input text-text-primary text-xs focus:outline-none focus:border-amber-500 transition-colors"
              />
            </div>
          </div>

          <div className="text-[10px] text-amber-500 dark:text-amber-400/90 font-mono">
            Диапазон: <span className="font-bold">U{uStart}..U{endU}</span> ({uHeight}U)
          </div>

          {/* Tenant */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">
              Владелец / Арендатор (Тенант)
            </label>
            <div className="flex gap-1.5 mb-1.5">
              {[
                { key: 'mine', label: 'Своя' },
                { key: 'colocation', label: 'Colocation' },
                { key: 'reserved', label: 'Буфер' },
              ].map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setTenant(chip.key)}
                  className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                    tenant === chip.key
                      ? 'bg-amber-500/20 border-amber-500 text-amber-600 dark:text-amber-300 font-bold'
                      : 'border-border text-text-secondary hover:border-border-focus bg-bg-input'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              placeholder="Например, ООО Иннотех / Client A"
              className="w-full px-2.5 py-1.5 rounded border border-border bg-bg-input text-text-primary text-xs focus:outline-none focus:border-amber-500 transition-colors"
            />
          </div>

          {/* Mounting Face */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">
              Плоскость монтажа
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'both', label: 'Вся глубина' },
                { id: 'front', label: 'Фронт' },
                { id: 'rear', label: 'Тыл' },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFace(f.id as any)}
                  className={`py-1 text-center text-[10px] font-bold rounded border transition-colors ${
                    face === f.id
                      ? 'bg-amber-500/20 border-amber-500 text-amber-600 dark:text-amber-300'
                      : 'border-border text-text-secondary hover:border-border-focus bg-bg-input'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">
              Назначение / Описание проекта
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Например, Резерв под проект K8s кластера"
              className="w-full px-2.5 py-1.5 rounded border border-border bg-bg-input text-text-primary text-xs focus:outline-none focus:border-amber-500 transition-colors"
            />
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded text-xs transition-colors text-text-secondary hover:bg-bg-hover"
            >
              Отмена
            </button>
            <button
              type="submit"
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded text-xs font-bold bg-amber-600 hover:bg-amber-500 text-white transition-colors shadow"
            >
              <Check size={14} />
              Забронировать
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
